import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Header.css";
import truerenewableslogo from "../../assets/truerenewables_logo.svg";
import trueCare360Img from "../../assets/true-care 360.png";
import ourSolutionsImg from "../../assets/ourSolutions.png";

const SO_ROUTES = {
  "PM-KUSUM Program": "/pm-kusum",
  "PM Surya Ghar Rooftop Yojana": "/rooftop-solar",
  "Captive Power Model": "/captive-model",
  "Group Captive Model": "/group-captive",
  "Open Access Solar": "/open-access",
  "Commercial & Industrial Rooftop": "/commercial-rooftop",
  "Residential Rooftop Systems": "/residential-rooftop",
};

const TC_ROUTES = {
  TrueTrack: "/truetrack",
  TrueCare: "/truecare",
  TrueCom: "/truecom",
};

// Reverse-lookup: path → item label
const PATH_TO_SO = Object.fromEntries(
  Object.entries(SO_ROUTES).map(([k, v]) => [v, k])
);
const PATH_TO_TC = Object.fromEntries(
  Object.entries(TC_ROUTES).map(([k, v]) => [v, k])
);

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive active items from current path
  const activeSOItem = PATH_TO_SO[location.pathname] || "";
  const activeTCItem = PATH_TO_TC[location.pathname] || "";

  const [menuOpen, setMenuOpen] = useState(false);
  const [truecareOpen, setTruecareOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);

  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileTruecareOpen, setMobileTruecareOpen] = useState(false);

  const closeTC = useRef(null);
  const closeSO = useRef(null);
  const headerRef = useRef(null);
  const truecareNavRef = useRef(null);
  const solutionsNavRef = useRef(null);

  const [tcLeft, setTcLeft] = useState(0);
  const [soLeft, setSoLeft] = useState(0);

  const TC_WIDTH = 587;
  const SO_WIDTH = 1062;

  const calcLeft = (navRef, cardWidth) => {
    if (!navRef.current || !headerRef.current) return 0;
    const navRect = navRef.current.getBoundingClientRect();
    const headerRect = headerRef.current.getBoundingClientRect();
    let left = navRect.left - headerRect.left;
    const rightEdge = navRect.left + cardWidth;
    if (rightEdge > window.innerWidth - 16) {
      left = left - (rightEdge - (window.innerWidth - 16));
    }
    if (left < 0) left = 0;
    return left;
  };

  // ─── Desktop hover handlers ────────────────────────────────────────────────
  const openTruecare = () => {
    if (closeTC.current) clearTimeout(closeTC.current);
    setTcLeft(calcLeft(truecareNavRef, TC_WIDTH));
    setTruecareOpen(true);
  };
  const closeTruecare = () => {
    closeTC.current = setTimeout(() => setTruecareOpen(false), 120);
  };
  const toggleTruecare = () => {
    if (truecareOpen) {
      setTruecareOpen(false);
    } else {
      if (closeTC.current) clearTimeout(closeTC.current);
      setTcLeft(calcLeft(truecareNavRef, TC_WIDTH));
      setTruecareOpen(true);
      setSolutionsOpen(false);
    }
  };

  const openSolutions = () => {
    if (closeSO.current) clearTimeout(closeSO.current);
    setSoLeft(calcLeft(solutionsNavRef, SO_WIDTH));
    setSolutionsOpen(true);
  };
  const closeSolutions = () => {
    closeSO.current = setTimeout(() => setSolutionsOpen(false), 120);
  };
  const toggleSolutions = () => {
    if (solutionsOpen) {
      setSolutionsOpen(false);
    } else {
      if (closeSO.current) clearTimeout(closeSO.current);
      setSoLeft(calcLeft(solutionsNavRef, SO_WIDTH));
      setSolutionsOpen(true);
      setTruecareOpen(false);
    }
  };

  // ─── Navigate helper — closes everything then navigates ───────────────────
  const goTo = (path) => {
    setTruecareOpen(false);
    setSolutionsOpen(false);
    setMenuOpen(false);
    setMobileSolutionsOpen(false);
    setMobileTruecareOpen(false);
    navigate(path);
  };

  // ─── Click outside to close ───────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setTruecareOpen(false);
        setSolutionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(closeTC.current);
      clearTimeout(closeSO.current);
    };
  }, []);

  const anyOpen = truecareOpen || solutionsOpen;

  return (
    <>
      {anyOpen && (
        <div
          className="dropdown-backdrop"
          onClick={() => {
            setTruecareOpen(false);
            setSolutionsOpen(false);
          }}
        />
      )}

      <header className="header" ref={headerRef}>
        <div className="header-container">
          {/* Logo → Home */}
          <div className="logo" onClick={() => goTo("/")}>
            <img src={truerenewableslogo} alt="Logo" />
          </div>

          {/* Desktop Nav */}
          <nav className="nav">
            <ul>
              <li
                ref={solutionsNavRef}
                onMouseEnter={openSolutions}
                onMouseLeave={closeSolutions}
                onClick={toggleSolutions}
                className={solutionsOpen ? "nav-active" : ""}
              >
                Our Solutions{" "}
                <span className={`arrow${solutionsOpen ? " arrow-open" : ""}`}>
                  <svg
                    width="12"
                    height="6"
                    viewBox="0 0 14 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 1L7 7L1 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </li>

              <li
                ref={truecareNavRef}
                onMouseEnter={openTruecare}
                onMouseLeave={closeTruecare}
                onClick={toggleTruecare}
                className={truecareOpen ? "nav-active" : ""}
              >
                TrueCare 360{" "}
                <span className={`arrow${truecareOpen ? " arrow-open" : ""}`}>
                  <svg
                    width="12"
                    height="6"
                    viewBox="0 0 14 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 1L7 7L1 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </li>

              <li onClick={() => goTo("/about")}>About Us</li>
            </ul>
          </nav>

          <div className="cta">
            <button onClick={() => goTo("/contact")}>
              Book A Free Call
            </button>
          </div>

          <div
            className="hamburger"
            onClick={() => {
              setMenuOpen(!menuOpen);
              if (menuOpen) {
                setMobileSolutionsOpen(false);
                setMobileTruecareOpen(false);
              }
            }}
          >
            {menuOpen ? "✕" : "☰"}
          </div>
        </div>

        {/* ── Desktop: TrueCare 360 Dropdown ─────────────────────────────── */}
        {truecareOpen && (
          <div
            className="tc-dropdown"
            style={{ left: `${tcLeft}px` }}
            onMouseEnter={openTruecare}
            onMouseLeave={closeTruecare}
          >
            <div className="tc-text">
              <p className="tc-cat-label">TRUECARE 360</p>
              <ul className="tc-list">
                {["TrueTrack", "TrueCare", "TrueCom"].map((item) => (
                  <li
                    key={item}
                    className={`tc-item${activeTCItem === item ? " tc-item--active" : ""}`}
                    onClick={() => goTo(TC_ROUTES[item])}
                  >
                    {activeTCItem === item && <span className="tc-dot" />}
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="tc-image-wrap">
              <img
                src={trueCare360Img}
                alt="TrueCare 360"
                className="tc-image"
                loading="lazy"
              />
            </div>
          </div>
        )}

        {/* ── Desktop: Our Solutions Dropdown ────────────────────────────── */}
        {solutionsOpen && (
          <div
            className="so-dropdown"
            style={{ left: `${soLeft}px` }}
            onMouseEnter={openSolutions}
            onMouseLeave={closeSolutions}
          >
            <div className="so-text">
              <div className="so-col">
                <p className="so-cat-label">CORPORATE ENERGY MODELS</p>
                <ul className="so-list">
                  {[
                    "Captive Power Model",
                    "Group Captive Model",
                    "Open Access Solar",
                  ].map((item) => (
                    <li
                      key={item}
                      className={`so-item${activeSOItem === item ? " so-item--active" : ""}`}
                      onClick={() => goTo(SO_ROUTES[item])}
                    >
                      {activeSOItem === item && <span className="so-dot" />}
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="so-cat-label so-cat-label--gap">NATIONAL SOLAR PROGRAMS</p>
                <ul className="so-list">
                  {["PM-KUSUM Program", "PM Surya Ghar Rooftop Yojana"].map(
                    (item) => (
                      <li
                        key={item}
                        className={`so-item${activeSOItem === item ? " so-item--active" : ""}`}
                        onClick={() => goTo(SO_ROUTES[item])}
                      >
                        {activeSOItem === item && <span className="so-dot" />}
                        {item}
                      </li>
                    )
                  )}
                </ul>
              </div>
              <div className="so-col">
                <p className="so-cat-label">ROOFTOP SOLAR SOLUTIONS</p>
                <ul className="so-list">
                  {[
                    "Commercial & Industrial Rooftop",
                    "Residential Rooftop Systems",
                  ].map((item) => (
                    <li
                      key={item}
                      className={`so-item${activeSOItem === item ? " so-item--active" : ""}`}
                      onClick={() => goTo(SO_ROUTES[item])}
                    >
                      {activeSOItem === item && <span className="so-dot" />}
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="so-image-wrap">
              <img
                src={ourSolutionsImg}
                alt="Our Solutions"
                className="so-image"
                loading="lazy"
              />
            </div>
          </div>
        )}

        {/* ── Mobile Menu ─────────────────────────────────────────────────── */}
        {menuOpen && (
          <div className="mobile-menu">
            <ul>
              {/* Our Solutions accordion */}
              <li
                className="mobile-menu-parent"
                onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
              >
                <span>Our Solutions</span>
                <span
                  className={`mobile-arrow${mobileSolutionsOpen ? " mobile-arrow-open" : ""}`}
                >
                  <svg
                    width="12"
                    height="7"
                    viewBox="0 0 14 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 1L7 7L1 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </li>
              {mobileSolutionsOpen && (
                <li className="mobile-submenu">
                  <div className="mobile-submenu-img-wrap">
                    <img
                      src={ourSolutionsImg}
                      alt="Our Solutions"
                      className="mobile-submenu-img"
                      loading="lazy"
                    />
                  </div>
                  <p className="mobile-sub-label">CORPORATE ENERGY MODELS</p>
                  <ul>
                    {[
                      "Captive Power Model",
                      "Group Captive Model",
                      "Open Access Solar",
                    ].map((item) => (
                      <li
                        key={item}
                        className={`mobile-sub-item${activeSOItem === item ? " mobile-sub-item--active" : ""}`}
                        onClick={() => goTo(SO_ROUTES[item])}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="mobile-sub-label">NATIONAL SOLAR PROGRAMS</p>
                  <ul>
                    {["PM-KUSUM Program", "PM Surya Ghar Rooftop Yojana"].map(
                      (item) => (
                        <li
                          key={item}
                          className={`mobile-sub-item${activeSOItem === item ? " mobile-sub-item--active" : ""}`}
                          onClick={() => goTo(SO_ROUTES[item])}
                        >
                          {item}
                        </li>
                      )
                    )}
                  </ul>
                  <p className="mobile-sub-label">ROOFTOP SOLAR SOLUTIONS</p>
                  <ul>
                    {[
                      "Commercial & Industrial Rooftop",
                      "Residential Rooftop Systems",
                    ].map((item) => (
                      <li
                        key={item}
                        className={`mobile-sub-item${activeSOItem === item ? " mobile-sub-item--active" : ""}`}
                        onClick={() => goTo(SO_ROUTES[item])}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </li>
              )}

              {/* TrueCare 360 accordion */}
              <li
                className="mobile-menu-parent"
                onClick={() => setMobileTruecareOpen(!mobileTruecareOpen)}
              >
                <span>TrueCare 360</span>
                <span
                  className={`mobile-arrow${mobileTruecareOpen ? " mobile-arrow-open" : ""}`}
                >
                  <svg
                    width="12"
                    height="7"
                    viewBox="0 0 14 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 1L7 7L1 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </li>
              {mobileTruecareOpen && (
                <li className="mobile-submenu">
                  <div className="mobile-submenu-img-wrap">
                    <img
                      src={trueCare360Img}
                      alt="TrueCare 360"
                      className="mobile-submenu-img"
                      loading="lazy"
                    />
                  </div>
                  <p className="mobile-sub-label">TRUECARE 360</p>
                  <ul>
                    {["TrueTrack", "TrueCare", "TrueCom"].map((item) => (
                      <li
                        key={item}
                        className={`mobile-sub-item${activeTCItem === item ? " mobile-sub-item--active" : ""}`}
                        onClick={() => goTo(TC_ROUTES[item])}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </li>
              )}

              <li onClick={() => goTo("/about")}>About Us</li>
              <li className="mobile-cta" onClick={() => goTo("/contact")}>
                Book A Free Call
              </li>
            </ul>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;